import { Controller } from "@hotwired/stimulus"

// Auto-calculates end time from route duration when From, To, and Start are
// all selected. Fetches duration from the server based on the location pair,
// then computes end_at = start_at + duration_minutes.
//
// Per-leg scope: each trip leg row has its own instance. Debounced to avoid
// flooding the server when quickly switching locations.
export default class extends Controller {
  static targets = ["fromLocation", "toLocation", "startAt", "endAt", "estimatedDuration", "endDisplay"]
  static values = { url: String }

  connect() {
    this.timer = null
    // On edit, bootstrap duration from the pre-populated hidden field
    // (Helm's EstimatedDuration, round-tripped via OrderFormMapper).
    const existing = parseInt(this.estimatedDurationTarget.value, 10)
    this.duration = existing > 0 ? existing : this.durationFromExistingTimes()

    // Self-heal (SBAU-590): a trip whose stored end is invalid (end <= start,
    // pre-SBAU-589 corruption) fails validation and blocks the WHOLE order
    // from saving — even legs the user didn't touch. When the trip's own span
    // can't provide a duration, fall back to the admin route-duration mapping
    // (async; fetchDuration ends in calculate(), which rewrites the end).
    // A valid dispatcher-set span is never overridden — the fetch only runs
    // when no duration could be derived at all.
    if (this.duration == null && this.fromLocationTarget.value && this.toLocationTarget.value) {
      this.fetchDuration()
    } else if (this.duration != null && this.storedEndInvalid()) {
      this.calculate()
    }
  }

  storedEndInvalid() {
    const start = new Date(this.startAtTarget.value)
    const end = new Date(this.endAtTarget.value)
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return false

    return end <= start
  }

  // Helm's GetOrder doesn't round-trip EstimatedDuration, so on edit the
  // hidden duration is usually blank — without a fallback, a start change
  // cleared the end and Helm kept the stale end time (SBAU-589). The trip's
  // own prefilled start→end span is the duration.
  durationFromExistingTimes() {
    const start = new Date(this.startAtTarget.value)
    const end = new Date(this.endAtTarget.value)
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null

    const minutes = Math.round((end - start) / 60000)
    return minutes > 0 ? minutes : null
  }

  disconnect() { clearTimeout(this.timer) }

  // Called when From or To changes — refetch duration
  locationChanged() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.fetchDuration(), 250)
  }

  // Called when Start changes — recalculate with cached duration
  startChanged() {
    this.calculate()
  }

  async fetchDuration() {
    const from = this.fromLocationTarget.value
    const to = this.toLocationTarget.value

    if (!from || !to) {
      this.duration = null
      this.clear()
      return
    }

    try {
      const params = new URLSearchParams({ from_location_id: from, to_location_id: to })
      const res = await fetch(`${this.urlValue}?${params}`, { headers: { Accept: "application/json" } })
      if (!res.ok) { this.duration = null; this.clear(); return }
      const body = await res.json()
      this.duration = body.duration_minutes
    } catch (_e) {
      this.duration = null
    }

    this.calculate()
  }

  calculate() {
    if (this.duration == null) { this.clear(); return }

    const startVal = this.startAtTarget.value
    if (!startVal) { this.clearDisplay(); this.fillHiddenDuration(); return }

    const startDate = new Date(startVal)
    if (isNaN(startDate.getTime())) { this.clearDisplay(); return }

    const endDate = new Date(startDate.getTime() + this.duration * 60 * 1000)

    // Fill hidden fields
    this.estimatedDurationTarget.value = this.duration
    this.endAtTarget.value = this.formatDatetimeLocal(endDate)

    // Show indicator
    const timeStr = endDate.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false })
    this.endDisplayTarget.innerHTML = `
      <span class="text-xs text-base-content/60 mt-1 inline-block">
        End: ${timeStr} AEST (${this.duration} min)
      </span>
    `
  }

  clear() {
    this.estimatedDurationTarget.value = ""
    this.endAtTarget.value = ""
    this.clearDisplay()
  }

  clearDisplay() {
    this.endDisplayTarget.innerHTML = ""
  }

  fillHiddenDuration() {
    if (this.duration != null) {
      this.estimatedDurationTarget.value = this.duration
    }
  }

  formatDatetimeLocal(date) {
    const pad = (n) => String(n).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  }
}
